import { makeStyles } from 'tss-react/mui';
import { alpha } from '@mui/material/styles';
import { AspectRatio, VerticalAlignment } from './report.model';
import { CSSProperties } from 'react';
import {
  REPORT_PDF_PAGE_HEIGHT_MM,
  REPORT_PDF_PAGE_WIDTH_MM,
} from '@shared/helpers/report-pdf-generation.helpers';
import { useGetScalingSize } from './report-style.helpers';
import {
  figmaPxToHeightPercent,
  REPORT_PAGE_DEFAULT_PADDING,
} from './report.constants';

/**
 * Gap between a thumbnail and the panel edge. The scroll gutter is reserved on
 * both edges at exactly this width, so the scrollbar lives in the gap instead
 * of taking content width — thumbnails stay centred whether or not the list
 * scrolls. Exported because the panel sizes its thumbnails from it.
 */
export const REPORT_THUMBNAIL_INSET = 8;
/** Inset of the skip toggle from the thumbnail's top-right corner. */
const THUMBNAIL_TOGGLE_INSET = 4;

type CSSJustifyContent = CSSProperties['justifyContent'];
type ReportPageStylesProps = {
  verticalAlign: VerticalAlignment;
  padding?: number;
  aspectRatio: AspectRatio;
  isFullscreen: boolean;
  hasHeader: boolean;
  hideFooter: boolean;
  getScalingSize: ReturnType<typeof useGetScalingSize>;
};

const alignmentMap: Record<VerticalAlignment, CSSJustifyContent> = {
  top: 'start',
  bottom: 'end',
  center: 'center',
  stretch: 'stretch',
};

const convertVerticalAlignmentToJustifyContent = (
  verticalAlign: VerticalAlignment,
): CSSProperties['justifyContent'] => alignmentMap[verticalAlign];

export const useReportPageStyles = makeStyles<ReportPageStylesProps>()((
  theme,
  {
    verticalAlign,
    padding = REPORT_PAGE_DEFAULT_PADDING,
    getScalingSize,
    aspectRatio,
    hasHeader,
    hideFooter,
  },
) => {
  const [ratioWidth, ratioHeight] = aspectRatio.split('/');
  return {
    reportPageRoot: {
      position: 'relative',
      // Identity transform keeps in-app positioning. Chrome's print pipeline
      // squashes glyphs inside any transformed ancestor, so print drops it.
      transform: 'translate(0, 0)',
      containerType: 'size',
      containerName: 'report-page-root',
      aspectRatio: aspectRatio,
      transition: 'aspect-ratio 0.3s ease-in-out',
      width: '100%',
      height: 'auto',
      // Sheet wider than 16:9: cap WIDTH so the page still fits. Size stays
      // width-driven (height: auto + aspect-ratio), never height: 100%.
      [`@container report-sheet (aspect-ratio > ${aspectRatio})`]: {
        width: `calc(100cqh * ${ratioWidth} / ${ratioHeight})`,
        maxWidth: '100%',
      },
      backgroundColor: theme.palette.warmSurface.main,
      '&:has(+ &)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '&:first-of-type': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      '[data-thumbnail] &, [data-thumbnail] &:first-of-type': {
        borderTop: 'none',
        borderBottom: 'none',
      },
      // Print sheets wrap one page each, so :first-of-type matches every page.
      '[data-report-print-sheet] &': {
        borderTop: 'none',
        borderBottom: 'none',
        transform: 'none',
      },
      '@media print': {
        border: 'none',
        transform: 'none',
        '&:has(~ [data-report-page])': {
          breakAfter: 'page',
        },
      },
    },
    reportPageFill: {
      position: 'absolute',
      inset: 0,
    },
    reportPage: {
      position: 'absolute',
      inset: 0,
      transform: 'translate(0, 0)',
      display: 'grid',
      gridTemplateRows: (() => {
        if (hideFooter) return hasHeader ? 'auto 1fr' : '1fr';
        return hasHeader ? 'auto 1fr auto' : '1fr auto';
      })(),
      padding: getScalingSize(padding),
      gap: padding > 0 ? getScalingSize(1) : 0,
      '[data-report-print-sheet] &': {
        transform: 'none',
      },
      '@media print': {
        transform: 'none',
      },
    },
    reportPageContentWrapper: {
      transform: 'translate(0, 0)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: convertVerticalAlignmentToJustifyContent(verticalAlign),
      overflow: 'visible',
      '[data-report-print-sheet] &': {
        transform: 'none',
      },
      '@media print': {
        transform: 'none',
      },
    },
    reportPageContent: {
      transform: 'rotate(0deg)',
      ...(verticalAlign === 'stretch' && { height: '100%' }),
      '[data-report-print-sheet] &': {
        transform: 'none',
      },
      '@media print': {
        transform: 'none',
      },
    },
    reportPageFooter: {
      minWidth: 0,
      width: '100%',
    },
    reportPageFooterOverlay: {
      // Column so the footer child stretches to the overlay width; a row
      // shrink-wraps it and the logo sits after the metadata instead of right.
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      position: 'absolute',
      left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
      right: `${REPORT_PAGE_DEFAULT_PADDING}%`,
      bottom: `${figmaPxToHeightPercent(24)}%`,
      zIndex: 1,
    },
  };
});

export const useReportStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((theme, { getScalingSize }) => ({
  report: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    '@media print': {
      display: 'block',
      height: 'auto',
      overflow: 'visible',
    },
  },
  reportPrintWrapper: {
    display: 'block',
    width: '100%',
    overflow: 'visible',
  },
  reportPrintSheet: {
    containerType: 'size',
    containerName: 'report-sheet',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: theme.palette.warmSurface.main,
    // One sheet per page. Break only when another sheet follows — a break after
    // the last sheet makes Chrome emit a trailing blank page.
    '&:has(+ &)': {
      breakAfter: 'page',
    },
    // Match `@page` so the 16:9 slide fills the sheet (not A4 letterbox).
    '@media print': {
      width: `${REPORT_PDF_PAGE_WIDTH_MM}mm`,
      height: `${REPORT_PDF_PAGE_HEIGHT_MM}mm`,
    },
  },
  reportToolbar: {
    padding: getScalingSize(0.5),
  },
  reportToolbarSlideCount: {
    paddingLeft: 4,
    fontSize: 12,
    lineHeight: 1,
    color: theme.palette.text.secondary,
    userSelect: 'none',
    whiteSpace: 'nowrap',
  },
  reportDefaultWrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.warmSurface.main,
    '@media print': {
      display: 'block',
      height: 'auto',
      overflow: 'visible',
    },
  },
  reportDefaultWrapperMain: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto',
    '@media print': {
      display: 'block',
      overflow: 'visible',
    },
  },
  reportPanel: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.warmSurface.dark,
    '@media print': {
      display: 'none',
    },
  },
  reportPanelToolbar: {
    flexShrink: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  reportPanelThumbnails: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarGutter: 'stable both-edges',
    // Narrower than the app-wide bar so the gutter matches the inset exactly.
    '&::-webkit-scrollbar': {
      width: REPORT_THUMBNAIL_INSET,
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingTop: 12,
    paddingBottom: 12,
  },
  reportResizeHandle: {
    width: 4,
    flexShrink: 0,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    transition: 'background-color 150ms',
    '&:hover': {
      backgroundColor: theme.palette.divider,
    },
    '@media print': {
      display: 'none',
    },
  },
  reportThumbnailWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover [data-thumbnail]': {
      borderColor: theme.palette.primary.main,
    },
    // The eye is a hover affordance on included slides; on excluded ones it
    // stays put (crossed out) so the panel shows what is left out at a glance.
    '&:hover [data-visibility-toggle]': {
      opacity: 1,
    },
    '&[data-hidden] [data-thumbnail]': {
      opacity: 0.35,
    },
    // The crossed-out eye stays put on a skipped slide, but without the plate:
    // the dimmed thumbnail is already the backdrop it needs.
    '&[data-hidden] [data-visibility-toggle]': {
      opacity: 1,
      backgroundColor: 'transparent',
    },
  },
  reportThumbnail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    aspectRatio: '16/9',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    pointerEvents: 'none',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 150ms, opacity 150ms',
  },
  reportThumbnailImage: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
  reportThumbnailLoader: {
    color: theme.palette.text.secondary,
  },
  reportThumbnailVisibility: {
    position: 'absolute',
    top: THUMBNAIL_TOGGLE_INSET,
    right: THUMBNAIL_TOGGLE_INSET,
    display: 'flex',
    opacity: 0,
    transition: 'opacity 150ms',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.warmSurface.main, 0.8),
    // Keyboard focus only. `:focus-within` also matched the focus a click
    // leaves behind, so the eye stayed visible on an unskipped thumbnail long
    // after the pointer had moved away.
    '&:has(:focus-visible)': {
      opacity: 1,
    },
  },
  reportFullscreenWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto 1fr auto',
    height: '100vh',
    width: '100vw',
    overflow: 'auto',
  },
  reportFullscreenHeader: {
    padding: '10px',
  },
  reportFullscreenFooter: {
    padding: '10px',
  },
  reportFullscreenContent: {
    containerType: 'size',
    containerName: 'report-sheet',
    resize: 'both',
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
